/**
 * 관리자 계정(admin_user) API 서비스.
 * full-access role 전용 — 목록/상세/CRUD/보안 액션/메뉴 권한/계정 감사 로그.
 */
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';

import type {
  AdminAuditLog,
  AdminMenuMetadataResponse,
  AdminMenuPermissionResponse,
  AdminMenuPermissionSavePayload,
  AdminUserCreatePayload,
  AdminUserCreateResponse,
  AdminUserDeletePayload,
  AdminUserDeleteResponse,
  AdminUserDetail,
  AdminUserEmailDuplicationResponse,
  AdminUserListParams,
  AdminUserSecurityActionPayload,
  AdminUserSummary,
  AdminUserTemporaryPasswordResetResponse,
  AdminUserUnlockResponse,
  AdminUserUpdatePayload,
  AdminUserUpdateResponse,
  MenuPermissionSaveItem,
  PageResponse,
} from '@/entities/admin-user/types';

/** undefined 값을 제외한 쿼리 파라미터 객체 */
const toQueryParams = (
  params: Record<string, string | number | boolean | undefined>,
): Record<string, string | number | boolean> => {
  const result: Record<string, string | number | boolean> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      result[key] = value;
    }
  });
  return result;
};

export const adminUserService = {
  /**
   * 관리자 계정 목록.
   * pageable은 Spring 기본 flat 쿼리(`page`, `size`)로 전달한다.
   */
  list: async (
    params: AdminUserListParams = {},
  ): Promise<PageResponse<AdminUserSummary>> => {
    const response = await apiClient.get(createApiPath.adminUsers.list(), {
      params: toQueryParams({
        query: params.query,
        roleName: params.roleName,
        status: params.status,
        includeDeleted: params.includeDeleted,
        page: params.page,
        size: params.size,
      }),
    });
    return response.data;
  },

  checkEmailDuplication: async (
    email: string,
  ): Promise<AdminUserEmailDuplicationResponse> => {
    const response = await apiClient.get(
      createApiPath.adminUsers.emailDuplication(),
      { params: { email } },
    );
    return response.data;
  },

  get: async (id: number): Promise<AdminUserDetail> => {
    const response = await apiClient.get(createApiPath.adminUsers.detail(id));
    return response.data;
  },

  create: async (
    body: AdminUserCreatePayload,
  ): Promise<AdminUserCreateResponse> => {
    const response = await apiClient.post(
      createApiPath.adminUsers.list(),
      body,
    );
    return response.data;
  },

  update: async (
    id: number,
    body: AdminUserUpdatePayload,
  ): Promise<AdminUserUpdateResponse> => {
    const response = await apiClient.patch(
      createApiPath.adminUsers.detail(id),
      body,
    );
    return response.data;
  },

  /** soft delete — body에 confirmEmail + rowVersion */
  remove: async (
    id: number,
    body: AdminUserDeletePayload,
  ): Promise<AdminUserDeleteResponse> => {
    const response = await apiClient.delete(
      createApiPath.adminUsers.detail(id),
      { data: body },
    );
    return response.data;
  },

  unlock: async (
    id: number,
    body: AdminUserSecurityActionPayload,
  ): Promise<AdminUserUnlockResponse> => {
    const response = await apiClient.post(
      createApiPath.adminUsers.unlock(id),
      body,
    );
    return response.data;
  },

  resetTemporaryPassword: async (
    id: number,
    body: AdminUserSecurityActionPayload,
  ): Promise<AdminUserTemporaryPasswordResetResponse> => {
    const response = await apiClient.post(
      createApiPath.adminUsers.temporaryPassword(id),
      body,
    );
    return response.data;
  },

  getMenuPermissions: async (
    id: number,
  ): Promise<AdminMenuPermissionResponse> => {
    const response = await apiClient.get(
      createApiPath.adminUsers.menuPermissions(id),
    );
    return response.data;
  },

  /** PUT 전체 교체 — 미포함 메뉴 override는 삭제되어 상속으로 복귀 */
  saveMenuPermissions: async (
    id: number,
    permissions: MenuPermissionSaveItem[],
  ): Promise<void> => {
    const body: AdminMenuPermissionSavePayload = { permissions };
    await apiClient.put(createApiPath.adminUsers.menuPermissions(id), body);
  },

  getMenuPermissionsMetadata: async (
    targetRoleName?: string,
  ): Promise<AdminMenuMetadataResponse> => {
    const response = await apiClient.get(
      createApiPath.adminUsers.menuPermissionsMetadata(),
      {
        params: targetRoleName ? { targetRoleName } : undefined,
      },
    );
    return response.data;
  },

  /**
   * 계정별 감사 로그 (최신순, 최대 200건).
   * OpenAPI상 페이징 파라미터가 없고 List로 반환한다.
   */
  getAuditLogs: async (id: number): Promise<AdminAuditLog[]> => {
    const response = await apiClient.get(
      createApiPath.adminUsers.auditLogs(id),
    );
    return response.data;
  },
};
